import { action, computed, makeObservable, observable } from 'mobx'

import { GrantTypesEnum } from '../enums/GrantTypes'
import IUserSession from '../interfaces/IUserSession'
import { getTokenPayload, isTokenAboutToExpire } from '../utils/JwtHelper'
import { safeGetItem, safeRemoveItem, safeSetItem } from '../utils/safeStorage'
import { setSentryUser } from '../utils/Sentry'
import { captureSilentLogout } from '../utils/silentLogoutTelemetry'
import ApiAuthService, { EmailAuth, PhoneAuth } from './ApiAuthService'

const STORAGE_KEY_SESSION = 'stx_user_session'

// Per-origin Web Lock name serializing refresh-token rotation across tabs.
const REFRESH_LOCK_NAME = 'stx-refresh-token'

// BroadcastChannel name on which the lock winner pushes its rotated tokens so
// queued waiters (and idle tabs) adopt them push-style, without their own
// /authenticate call. Falls back to the `storage` event where BroadcastChannel
// is absent — the same degradation population as the Web Lock (STX1-616).
const AUTH_BROADCAST_CHANNEL = 'stx-auth'

interface IRotatedTokensMessage {
  type: 'rotated'
  accessToken: string
  refreshToken: string
}

type AuthStateCallback = (session: IUserSession | null) => void

/**
 * Unified auth service replacing FirebaseAuthService.
 * All authentication flows go through the STX Auth API.
 * Manages session persistence in localStorage with multi-tab sync.
 */
export default class StruxHubAuthService {
  private static instance: StruxHubAuthService | null = null

  @observable private _session: IUserSession | null = null
  private listeners = new Set<AuthStateCallback>()
  private refreshingToken: Promise<string> | null = null
  private broadcastChannel: BroadcastChannel | null = null

  public constructor(private readonly apiService: ApiAuthService) {
    if (StruxHubAuthService.instance) {
      throw new Error(
        'StruxHubAuthService already instantiated — use the existing instance',
      )
    }
    StruxHubAuthService.instance = this
    makeObservable(this)
    this.setupStorageSync()
    this.setupBroadcastSync()
  }

  // --- Session accessors ---

  @computed
  public get session(): IUserSession | null {
    return this._session
  }

  @computed
  public get isAuthenticated(): boolean {
    return !!this._session?.accessToken
  }

  public get currentUser() {
    if (!this._session?.accessToken) return null
    return getTokenPayload(this._session.accessToken)
  }

  public get isFromSSO(): boolean {
    return this.currentUser?.auth_method === 'sso'
  }

  // --- Login methods ---

  public async loginWithEmailPassword(
    email: string,
    password: string,
  ): Promise<IUserSession> {
    const authData: EmailAuth = {
      email,
      password,
      grantType: GrantTypesEnum.password,
    }
    const result = await this.apiService.logInViaStruxhub(authData)
    if (!result) {
      throw new Error(
        'Server encountered an error. Make sure you entered your credentials correctly',
      )
    }
    return this.createSession(result.accessToken, result.refreshToken)
  }

  public async loginWithPhoneOTP(
    phone: string,
    oneTimeCode: string,
    nonsense: string,
  ): Promise<IUserSession> {
    const authData: PhoneAuth = {
      phone,
      oneTimeCode,
      nonsense,
      grantType: GrantTypesEnum.authorization_code,
    }
    const result = await this.apiService.logInViaStruxhub(authData)
    if (!result) {
      throw new Error('Phone authentication failed')
    }
    return this.createSession(result.accessToken, result.refreshToken)
  }

  /**
   * Login using invite key. Uses the STX accessToken from the response.
   */
  public async loginWithInviteKey(
    inviteKey: string,
    tenantHostName?: string,
  ): Promise<IUserSession> {
    const { data } = await this.apiService.loginWithInviteKey(
      inviteKey,
      tenantHostName,
    )
    return this.createSession(data.accessToken, data.refreshToken)
  }

  /**
   * Login with a session handoff code (STX1-495). Sends the current session's
   * token if there is one; the server keeps that session when it is the code's
   * account, otherwise returns a fresh pair.
   */
  public async loginWithHandoffCode(code: string): Promise<IUserSession> {
    if (!this._session) {
      this.restoreSession()
    }
    const accessToken = await this.getValidAccessToken().catch(() => null)

    // A failed exchange leaves the stored session alone: the session is per
    // domain, so the caller boots it (or login) and no other tab is touched.
    const result = await this.apiService.exchangeHandoffCode(
      code,
      accessToken ?? undefined,
    )

    if (result.reused && this._session) {
      return this._session
    }
    if (!result.accessToken || !result.refreshToken) {
      throw new Error('400')
    }
    return this.createSession(result.accessToken, result.refreshToken)
  }

  /**
   * Mint a session handoff code for the current session (STX1-495).
   * `targetHost` null = the login host (User Portal).
   */
  public async mintHandoffCode(targetHost?: string): Promise<string> {
    const accessToken = await this.getValidAccessToken()
    if (!accessToken) {
      throw new Error('401')
    }
    return this.apiService.mintHandoffCode(accessToken, targetHost)
  }

  /**
   * Complete login with existing STX tokens (e.g., from SSO callback or mobile WebView).
   */
  public loginWithTokens(
    accessToken: string,
    refreshToken: string,
  ): IUserSession {
    return this.createSession(accessToken, refreshToken)
  }

  // --- Token management ---

  public getValidAccessToken = async (): Promise<string> => {
    if (!this._session) return null
    if (!isTokenAboutToExpire(this._session.accessToken)) {
      return this._session.accessToken
    }
    return this.refreshAccessToken()
  }

  /**
   * Force a refresh regardless of token-expiry gating. Public so Apollo's
   * onError refresh link can drive a refresh on 401 from the GraphQL path —
   * `getValidAccessToken` short-circuits when the token is not yet
   * about-to-expire, so 401-driven retries need this entry point.
   */
  public async refreshAccessToken(): Promise<string> {
    // Layer 1 — in-tab dedup. Collapses a same-tab burst (WS reconnect storm,
    // parallel graph ops, simultaneous REST + GraphQL 401) into one promise
    // that contends for the cross-tab lock exactly once.
    if (this.refreshingToken) {
      return this.refreshingToken
    }

    // Snapshot the access token we are dissatisfied with (the about-to-expire
    // one, or the one the server just 401'd) BEFORE contending for the lock.
    // doRefreshToken adopts a stored access token only if it has ADVANCED past
    // this snapshot — i.e. a peer actually rotated one in. Keying on the access
    // token (not the refresh token) closes STX1-616 both ways: a queued tab
    // whose storage/BroadcastChannel handler already adopted the winner's
    // rotation sees a changed access token and skips the redundant POST (true
    // single-flight), while the forced-401 path — access token unchanged because
    // no peer rotated — still refreshes instead of re-adopting the just-rejected
    // token.
    const intendedAccessToken = this._session?.accessToken ?? null
    this.refreshingToken = this.withRefreshLock(() =>
      this.doRefreshToken(intendedAccessToken),
    )
    try {
      return await this.refreshingToken
    } finally {
      this.refreshingToken = null
    }
  }

  /**
   * Layer 2 — cross-tab serialization via the Web Locks API. Only one tab of a
   * given origin holds 'stx-refresh-token' at a time, so concurrent same-origin
   * tabs can't both POST /authenticate with the same refresh token. Web Locks
   * auto-release when the holder's promise settles AND when the holding context
   * dies (tab close/crash), so a crashed tab can't deadlock the others; the
   * only unbounded case — a live tab whose refresh request hangs — is bounded
   * one layer down by the request timeout in ApiAuthService.getFreshStruxhubTokens.
   * Where navigator.locks is absent (jsdom tests, SSR, Safari <15.4) it runs
   * unguarded — the per-tab-dedup-only behaviour, so no regression.
   */
  private withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return fn()
    }
    return navigator.locks.request(REFRESH_LOCK_NAME, fn)
  }

  private async doRefreshToken(
    intendedAccessToken: string | null,
  ): Promise<string> {
    if (!this._session) return null

    // Single-flight adopt-or-refresh (STX1-616). The cross-tab Web Lock is now
    // held, so a tab that raced a winner observes the winner's SETTLED result:
    // if storage now carries a fresh access token that DIFFERS from the one we
    // came in with, a peer rotated while we queued — ADOPT it and skip the
    // network entirely. This is the no-loser invariant: "only POST if no peer
    // has already produced a fresh token for us."
    const adopted = this.tryAdoptFreshStoredSession(intendedAccessToken)
    if (adopted) return adopted

    // No fresh peer rotation visible. We hold the lock, so we are the single
    // context allowed to spend the refresh token. No client-side expiry
    // prediction (STX1-556): the server is authoritative on refresh-token
    // lifetime; a genuinely-dead token returns null and we log out below.
    const refreshTokenToSpend = this._session.refreshToken
    const result = await this.apiService.getFreshStruxhubTokens({
      refreshToken: refreshTokenToSpend,
      grantType: GrantTypesEnum.refresh_token,
    })

    if (result) {
      this.updateSession(result.accessToken, result.refreshToken)
      // Push the rotation to other tabs so queued waiters / idle tabs adopt it
      // without their own /authenticate call.
      this.broadcastRotatedTokens(result.accessToken, result.refreshToken)
      return result.accessToken
    }

    // Verify-after-401 INSIDE the held lock (STX1-616, the no-loser guarantee).
    // A failed refresh while we hold the lock means we raced a tab that
    // finished and persisted its rotation just before we acquired — the lock
    // makes the winner's completion a happens-before edge, so a single
    // deterministic re-read resolves it. No polling, no backoff timers: re-read
    // persisted storage once more and adopt any fresh token a peer left behind.
    const recovered = this.tryAdoptFreshStoredSession(intendedAccessToken)
    if (recovered) return recovered

    // No fresher token anywhere — the refresh token is genuinely dead (true
    // 30-day expiry / unknown token). This is the single non-polling terminal
    // branch; a genuinely-dead token still logs the user out.
    console.warn('doRefreshToken: /authenticate failed, logging out')
    this.logout()
    throw new Error('Failed to refresh token')
  }

  /**
   * Adopt the persisted session if a peer tab won the lock, rotated, and
   * persisted a FRESH access token before us. The gate keys on the access token
   * having ADVANCED past `intendedAccessToken` (the token we entered the refresh
   * with), NOT on the refresh token differing from our in-memory copy: a queued
   * tab whose storage/BroadcastChannel handler already adopted the winner's
   * rotation has `_session.refreshToken === stored.refreshToken`, so the old
   * gate missed it and POSTed again — defeating single-flight (STX1-616).
   * Comparing the access token against the pre-lock snapshot also keeps the
   * forced-401 path honest: there the access token is unchanged (no peer
   * rotated), so we refresh rather than re-adopt the token the server just
   * rejected. Guarded by `!isTokenAboutToExpire` so we never adopt a stale token
   * and skip a refresh we actually need. Returns the adopted access token, or
   * null if no fresher peer rotation is visible. The session must still exist; a
   * concurrent logout clears it.
   */
  private tryAdoptFreshStoredSession(
    intendedAccessToken: string | null,
  ): string | null {
    if (!this._session) return null
    const stored = this.readStoredSession()
    if (
      stored?.accessToken &&
      stored.refreshToken &&
      stored.accessToken !== intendedAccessToken &&
      !isTokenAboutToExpire(stored.accessToken)
    ) {
      this.adoptRotatedTokens(stored.accessToken, stored.refreshToken)
      return stored.accessToken
    }
    return null
  }

  // --- Auth state observation ---

  /**
   * Register a callback to be notified on auth state changes.
   * Returns an unsubscribe function.
   */
  public onAuthStateChanged(callback: AuthStateCallback): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  private notifyListeners() {
    const session = this._session
    for (const cb of this.listeners) {
      try {
        cb(session)
      } catch (error) {
        console.error('Auth state listener error:', error)
      }
    }
  }

  // --- Logout ---

  @action
  public logout() {
    this.setSession(null)
    this.refreshingToken = null
    safeRemoveItem(STORAGE_KEY_SESSION)
    // No tenant-identity sessionStorage to clear: login context lives on
    // the URL (return_to), navigation-scoped, and the logout bounce
    // emits none of it (STX1-540). Cross-user leakage is structural now,
    // not a clear-on-logout contract (the STX1-539 fix).
    this.apiService.resetAccessToken()
    this.notifyListeners()
  }

  // --- Session management ---

  @action
  private setSession(session: IUserSession | null): void {
    this._session = session
    setSentryUser(
      session
        ? {
            id: session.nameId ?? getTokenPayload(session.accessToken)?.sub,
            email: session.email,
          }
        : null,
    )
  }

  @action
  private createSession(
    accessToken: string,
    refreshToken: string,
  ): IUserSession {
    const payload = getTokenPayload(accessToken)
    const session: IUserSession = {
      accessToken,
      refreshToken,
      nameId: payload?.nameid,
      email: payload?.email,
      tenantHostOverride: null,
    }
    this.setSession(session)
    this.apiService.setAccessToken(accessToken)
    this.persistSession()
    this.notifyListeners()
    return session
  }

  @action
  private updateSession(accessToken: string, refreshToken: string) {
    if (!this._session) return
    this._session.accessToken = accessToken
    this._session.refreshToken = refreshToken
    this.apiService.setAccessToken(accessToken)
    this.persistSession()
  }

  /**
   * Adopt tokens a peer tab already rotated and persisted. Mutates _session in
   * place (like updateSession) so initialState.tenantUserSession, which aliases
   * this._session after restore, stays in sync — replacing the object instead
   * would strand the alias on the stale refresh token. Does not re-persist (the
   * peer is the source of truth) and leaves tenantHostOverride as this tab's own
   * navigation context.
   */
  @action
  private adoptRotatedTokens(accessToken: string, refreshToken: string) {
    if (!this._session) return
    this._session.accessToken = accessToken
    this._session.refreshToken = refreshToken
    this.apiService.setAccessToken(accessToken)
  }

  /**
   * Persist an explicit tenant host to operate as, for envs where the
   * browser origin doesn't identify the tenant (preview envs, mobile
   * WebView, localhost dev). See {@link IUserSession.tenantHostOverride}.
   */
  @action
  public setTenantHostOverride(host: string) {
    if (!this._session) return
    this._session.tenantHostOverride = host
    this.persistSession()
  }

  /**
   * Drop the persisted tenantHostOverride without ending the session. Used by
   * the "Choose another tenant" recovery path in Redirector: the user is
   * still authenticated but the previously-picked tenant is unreachable, so
   * we must clear the persisted host so the next page-load doesn't re-apply
   * it via `restoreSession()` → `initAuthUser()` → `setActiveTenant(host)`
   * (the loop STX1-539 review caught). Symmetric counterpart to
   * setTenantHostOverride; persists the cleared field.
   */
  @action
  public clearTenantHostOverride() {
    if (!this._session) return
    this._session.tenantHostOverride = null
    this.persistSession()
  }

  /** Read the persisted session without mutating in-memory state. */
  private readStoredSession(): IUserSession | null {
    try {
      const stored = safeGetItem(STORAGE_KEY_SESSION)
      return stored ? (JSON.parse(stored) as IUserSession) : null
    } catch {
      return null
    }
  }

  /**
   * Whether a persisted session with an access token exists in storage.
   * A pure read (no in-memory mutation) — lets a route tell a not-yet-
   * hydrated session apart from a genuinely absent one on cold load, so a
   * direct navigation to a gated page can wait for `initAuthUser` to
   * restore the session instead of bouncing to /login.
   */
  public hasStoredSession(): boolean {
    return !!this.readStoredSession()?.accessToken
  }

  private persistSession() {
    if (this._session) {
      safeSetItem(STORAGE_KEY_SESSION, JSON.stringify(this._session))
    } else {
      safeRemoveItem(STORAGE_KEY_SESSION)
    }
  }

  /**
   * Restore session from localStorage on page load.
   * Returns the restored session or null if no valid session exists.
   */
  @action
  public restoreSession(): IUserSession | null {
    try {
      const stored = safeGetItem(STORAGE_KEY_SESSION)
      if (!stored) return null

      const session: IUserSession = JSON.parse(stored)
      if (!session?.accessToken) return null

      // No client-side expiry prediction (STX1-556). Restore the session
      // optimistically; if the refresh token is genuinely dead, the very
      // first protected request will 401 → Apollo/REST refresh link →
      // doRefreshToken → server returns null → logout(). The brief
      // optimistic flash is the right trade vs synchronously bouncing a
      // user whose token would in fact still refresh server-side.
      this.setSession(session)
      this.apiService.setAccessToken(session.accessToken)
      return session
    } catch {
      console.warn('Session restore: localStorage session unparseable')
      safeRemoveItem(STORAGE_KEY_SESSION)
      return null
    }
  }

  // --- Multi-tab sync ---

  private setupStorageSync() {
    if (typeof window === 'undefined') return

    window.addEventListener('storage', event => {
      if (event.key !== STORAGE_KEY_SESSION) return

      if (!event.newValue) {
        // Another tab logged out. Fire a Sentry event because this is
        // the most surprising silent path — the user did nothing in
        // this tab but the next graph effect will dispatch LOGOUT once
        // _session is cleared. Capture only when we actually had a
        // session to begin with so unrelated storage-clears (tests,
        // devtools, fresh load) don't pollute the metric.
        if (this._session) {
          captureSilentLogout('storage-sync-cleared')
        }
        this.setSession(null)
        this.apiService.resetAccessToken()
        this.notifyListeners()
        return
      }

      try {
        const session: IUserSession = JSON.parse(event.newValue)
        this.setSession(session)
        this.apiService.setAccessToken(session.accessToken)
        this.notifyListeners()
      } catch {
        // Ignore invalid data from other tabs
      }
    })
  }

  /**
   * Push-based cross-tab rotation adoption (STX1-616). The lock winner posts
   * its rotated tokens here; queued waiters and idle tabs adopt them
   * immediately instead of waiting on a `storage`-event/re-read race or running
   * their own refresh. BroadcastChannel is the primary transport; the `storage`
   * event in setupStorageSync remains the fallback where it is absent (same
   * degradation population as the Web Lock — Safari <15.4, jsdom, SSR).
   */
  private setupBroadcastSync() {
    if (typeof BroadcastChannel === 'undefined') return

    try {
      this.broadcastChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL)
    } catch {
      this.broadcastChannel = null
      return
    }

    this.broadcastChannel.onmessage = event => {
      const data = event.data as IRotatedTokensMessage | null
      if (!data || data.type !== 'rotated') return
      // Adopt only into a live session, and only a token we don't already hold.
      if (!this._session) return
      if (
        !data.accessToken ||
        !data.refreshToken ||
        data.refreshToken === this._session.refreshToken
      ) {
        return
      }
      // Revalidate against the persisted session before adopting. The lock
      // winner PERSISTS its rotation before it broadcasts, so a live rotation
      // always matches storage; a stale or out-of-order broadcast (a token a
      // peer has already rotated past — BroadcastChannel has no cross-sender
      // ordering guarantee) will NOT match and must be ignored. Adopting it
      // would roll this tab back onto an already-spent token. Keying on the
      // storage authority is what actually backs the "never downgrade"
      // guarantee, rather than the bare refresh-token inequality above.
      const stored = this.readStoredSession()
      if (
        stored?.accessToken !== data.accessToken ||
        stored?.refreshToken !== data.refreshToken
      ) {
        return
      }
      this.adoptRotatedTokens(data.accessToken, data.refreshToken)
      this.notifyListeners()
    }
  }

  /** Broadcast a fresh rotation so other tabs adopt it without a refresh call. */
  private broadcastRotatedTokens(accessToken: string, refreshToken: string) {
    if (!this.broadcastChannel) return
    try {
      const message: IRotatedTokensMessage = {
        type: 'rotated',
        accessToken,
        refreshToken,
      }
      this.broadcastChannel.postMessage(message)
    } catch {
      // Channel closed / serialization failure — the storage event still
      // propagates the persisted session as the fallback path.
    }
  }
}
